import { Feature, type Map } from "ol";
import type PointGeometry from "ol/geom/Point";
import { store } from "../../../../stateManagement/store";
import { setTransportMonitoringCheckReady, setTransportMonitoringLastShownFeaturesJson, setTransportMonitoringObject } from "../../../../stateManagement/reducers/transport/TransportNowSlice";
import { Point } from "ol/geom";
import { fromLonLat, toLonLat } from "ol/proj";
import GeoJSON from "ol/format/GeoJSON";
import { showToastMessage } from "../../../../stateManagement/reducers";
import { transportMonitoringIconHitRadiusPx } from "../../../../widgets/map/mapFeatures/styles/mapStyles";
import { removeOverlayFromStateById, upsertOverlayInState } from "../../../../widgets/map/mapFeatures/mapFunctions/addOverlays/overlayState";

const TRANSPORT_OVERLAY_ID = "transport-now-hover";
let lastTransportOverlayKey: string | null = null;

function resetTransportOverlay() {
    if (lastTransportOverlayKey === null) return;

    lastTransportOverlayKey = null;
    removeOverlayFromStateById(TRANSPORT_OVERLAY_ID);
}

function getTransportOverlayTitle(properties: Record<string, any>) {
    return properties?.Name ?? properties?.name ?? properties?.caption ?? properties?.Caption ?? properties?.GarageNumber ?? properties?.RegNumber;
}

function buildTransportOverlayFields(properties: Record<string, any>) {
    return [
        { label: "Идентификатор ТС", value: properties?.objectId ?? properties?.id },
        { label: "Скорость, км/ч", value: properties?.speed },
        { label: "Азимут", value: properties?.azimuth },
    ]
        .filter((field) => field.value !== undefined && field.value !== null && field.value !== "")
        .map((field) => ({
            label: field.label,
            value: String(field.value),
        }));
}

function buildTransportOverlayPayload(feature: any, map: Map) {
    const { geometry, ...properties } = feature?.getProperties?.() ?? {};
    const pointGeometry = geometry as PointGeometry | undefined;
    const coordinates = pointGeometry?.getCoordinates?.();
    const projection = map.getView()?.getProjection();

    if (!Array.isArray(coordinates) || coordinates.length < 2 || !projection) return null;

    const [longitude, latitude] = toLonLat(coordinates, projection);
    const fields = buildTransportOverlayFields(properties);
    if (!Number.isFinite(longitude) || !Number.isFinite(latitude) || fields.length === 0) return null;

    return {
        title: getTransportOverlayTitle(properties),
        position: { longitude, latitude },
        data: { fields },
    };
}

function pointerIsOverTransportIcon(feature: any, event: any, map: Map) {
    const geometry = feature?.getGeometry?.();
    const coordinate = geometry?.getCoordinates?.();
    if (!Array.isArray(coordinate) || coordinate.length < 2) return false;

    const featurePixel = map.getPixelFromCoordinate(coordinate);
    const pointerPixel = map.getEventPixel(event.originalEvent);
    if (!Array.isArray(featurePixel) || !Array.isArray(pointerPixel)) return false;

    const dx = featurePixel[0] - pointerPixel[0];
    const dy = featurePixel[1] - pointerPixel[1];

    return Math.hypot(dx, dy) <= transportMonitoringIconHitRadiusPx;
}

export function handleTransportSocketOpened() {
    const statusCircle = document.querySelector('.server-status-circle')
    statusCircle?.classList.remove('disconnected');
    statusCircle?.classList.add('connected')
}

export function handleTransportSocketClosed() {
    console.warn("Connection is closed");
    const statusCircle = document.querySelector('.server-status-circle')
    statusCircle?.classList.remove('connected')
    statusCircle?.classList.add('disconnected');
}

export function handleTransportSocketError() {
    console.warn(new Error("Websocket is null, probably it was closed"))
    const statusCircle = document.querySelector('.server-status-circle')
    statusCircle?.classList.remove('connected')
    statusCircle?.classList.add('disconnected');
}

export function hoverMapForTransportNow(event: any, map: Map) {
    if (event.dragging) {
        resetTransportOverlay();
        return;
    }

    if (!store.getState().contentHeader.mapTooltipIsEnabled) {
        resetTransportOverlay();
        return;
    }

    const pixel = map?.getEventPixel(event.originalEvent);
    const feature: any = map?.forEachFeatureAtPixel(pixel, function (f: any) {
        return f?.getProperties?.()?.showTransportBadge ? f : null;
    });

    if (!feature || !pointerIsOverTransportIcon(feature, event, map)) {
        resetTransportOverlay();
        return;
    }

    const overlayPayload = buildTransportOverlayPayload(feature, map);
    if (!overlayPayload) {
        resetTransportOverlay();
        return;
    }

    const nextOverlayKey = JSON.stringify(overlayPayload);
    if (lastTransportOverlayKey === nextOverlayKey) return;

    lastTransportOverlayKey = nextOverlayKey;
    upsertOverlayInState(
        {
            id: TRANSPORT_OVERLAY_ID,
            type: "transportInfo",
            position: overlayPayload.position,
            data: {
                title: overlayPayload.title,
                fields: overlayPayload.data.fields,
            },
        }
    );
}

export function clickMapForTransportNow(event: any, map: Map, navigate: any) {
    var pixel = map?.getEventPixel(event.originalEvent);
    var feature = map?.forEachFeatureAtPixel(pixel, function (f) { return f; });
    if (feature) {
        let id = feature.getProperties()?.objectId
        if (id) {
            navigate(`/app/transport/now/${id}`)
        } else {
            store.dispatch(showToastMessage({ messageText: "Карточка транспортного средства не заполнена", messageType: "info", windowType: "map" }))
        }
    }
}

export function configureTransportMoveFeaturesList(transportMonitoringObject: any, transportLayer: any, transportMonitoringCheckReady: any) {
    let newTransportLayer = transportLayer
    if (transportMonitoringObject.type && transportMonitoringObject.type === "VEHICLES") { // Пришли данные о ТС на карте
        const statusCircle = document.querySelector('.server-status-circle')
        statusCircle?.classList.remove('disconnected');
        statusCircle?.classList.remove('connected')
        statusCircle?.classList.add('connection')
        let timeout = setTimeout(function () {
            statusCircle?.classList.remove('connection')
            statusCircle?.classList.add('connected')
            clearTimeout(timeout);
        }, 2000);
        let vehicles = store.getState().transportNow.transportMonitoringFullList
        let moveList = transportMonitoringObject.move;
        if (vehicles) {
            moveList.map((item: any) => {
                let props: any = {}
                let existingFeature = newTransportLayer.getSource().getFeatureById(item.objectId);
                if (existingFeature === null) {
                    // Такого идентификатора не было - добавляем
                    existingFeature = new Feature({
                        geometry: new Point(fromLonLat([item.lon, item.lat]))
                    });
                    props = (vehicles.filter(function (value: any) {
                        return value.objectId == item.objectId
                    }))[0];
                    props = props == null ? {} : props;
                    let newProps = props.hasOwnProperty("objectId")
                        ? Object.assign({}, { ...props, id: props.objectId, azimuth: item.azimuth, speed: item.speed, showTransportBadge: true })
                        : Object.assign(props, { ...props, id: item.objectId, azimuth: item.azimuth, speed: item.speed, showTransportBadge: true });
                    existingFeature.setProperties(newProps);
                    existingFeature.setId(newProps.id);
                    newTransportLayer.getSource().addFeature(existingFeature);
                } else {
                    // Маркер есть на карте - надо его переместить
                    existingFeature.setGeometry(new Point(fromLonLat([item.lon, item.lat])));
                    // Обновить скорость и направление движения
                    props = existingFeature.getProperties();
                    props.azimuth = item.azimuth;
                    props.speed = item.speed;
                    existingFeature.setProperties(props);
                }
            })
        }
        // удалить метки с карты
        let removeList = transportMonitoringObject.remove;
        removeList.forEach(function (item: any) {
            let existingFeature = newTransportLayer.getSource().getFeatureById(item);
            if (existingFeature) {
                newTransportLayer.getSource().removeFeature(existingFeature);
            }
        });
    }
    var vectorLayerAsJson = new GeoJSON().writeFeaturesObject(newTransportLayer.getSource().getFeatures());
    store.dispatch(setTransportMonitoringObject(null))
    store.dispatch(setTransportMonitoringLastShownFeaturesJson(vectorLayerAsJson))
    store.dispatch(setTransportMonitoringCheckReady(!transportMonitoringCheckReady))
    return newTransportLayer
}
